import React, { useState, useRef, useCallback } from 'react';
import propertyViewingsService from '../services/propertyViewingsService';
import './Suggestions.css';

const PropertyViewingForm = React.memo(({ user, users, onSubmit, viewing = {}, isEdit = false }) => {
  console.log('🔍 [PROPERTY FORM] Component render - viewing:', viewing, 'isEdit:', isEdit);
  
  // State for selected guardians (Laravel-style list)
  const [selectedGuardians, setSelectedGuardians] = useState([]);
  const selectedGuardiansRef = useRef([]);
  
  // Ref for guardian search input to maintain focus
  const guardianInputRef = useRef(null);
  // Ref for guardian suggestions div
  const guardianSuggestionsRef = useRef(null);
  
  // Custom date picker state (to prevent native picker from setting date on arrow click)
  const [showDayPicker, setShowDayPicker] = useState(false);
  const [dayPickerMonth, setDayPickerMonth] = useState(new Date().getMonth() + 1);
  const [dayPickerYear, setDayPickerYear] = useState(new Date().getFullYear());
  const dayPickerRef = useRef(null);
  
  // QUICK FIX: Use refs to store all form values to prevent resets on re-render
  const formFieldsRef = useRef({
    property_id: viewing.property_id || '',
    property_title: viewing.property_title || '',
    user_id: user?.id || viewing.user_id || '',
    attendees: viewing.attendees || '',
    rooms_taken: viewing.rooms_taken || '',
    day: viewing.day ? (viewing.day.includes('/') ? viewing.day.split('/').reverse().join('-') : viewing.day) : '',
    time_from: viewing.time_from || '',
    time_to: viewing.time_to || '',
    guardian_title: viewing.guardian_title || '',
    notes: viewing.notes || '',
    confirmed: viewing.confirmed || ''
  });
  
  // Ref for property input to preserve value
  const propertyInputRef = useRef(null);
  
  // Form data state for controlled components (but we'll sync with refs)
  const [formData, setFormData] = useState(() => formFieldsRef.current);
  
  const formDataRef = useRef(formData);
  
  // Keep refs in sync with state
  React.useEffect(() => {
    selectedGuardiansRef.current = selectedGuardians;
  }, [selectedGuardians]);
  
  React.useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  // Only update formData when viewing changes in edit mode, but preserve existing values
  // IMPORTANT: Don't reset formData on every viewing change - only update specific fields if they're different
  React.useEffect(() => {
    if (isEdit && viewing.user_id && viewing.user_id !== formData.user_id) {
      setFormData(prev => ({ ...prev, user_id: viewing.user_id }));
    }
  }, [isEdit, viewing.user_id, formData.user_id]); // Only update if user_id actually changed
  
  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (guardianSearchTimeoutRef.current) {
        clearTimeout(guardianSearchTimeoutRef.current);
      }
    };
  }, []);
  
  // Click outside handler for day picker
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dayPickerRef.current && !dayPickerRef.current.contains(event.target)) {
        setShowDayPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Calendar generation functions
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  
  const generateCalendarDays = (month, year) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };
  
  const handlePrevMonth = () => {
    if (dayPickerMonth === 1) {
      setDayPickerMonth(12);
      setDayPickerYear(dayPickerYear - 1);
    } else {
      setDayPickerMonth(dayPickerMonth - 1);
    }
  };
  
  const handleNextMonth = () => {
    if (dayPickerMonth === 12) {
      setDayPickerMonth(1);
      setDayPickerYear(dayPickerYear + 1);
    } else {
      setDayPickerMonth(dayPickerMonth + 1);
    }
  };
  
  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value);
    if (!isNaN(newYear)) {
      setDayPickerYear(newYear);
    }
  };
  
  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value);
    if (!isNaN(newMonth) && newMonth >= 1 && newMonth <= 12) {
      setDayPickerMonth(newMonth);
    }
  };
  
  const handleDaySelect = (day) => {
    if (day === null) return;
    const month = String(dayPickerMonth).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const year = dayPickerYear;
    // Store in YYYY-MM-DD format (for date input compatibility)
    const dateValue = `${year}-${month}-${dayStr}`;
    
    formFieldsRef.current.day = dateValue;
    handleFieldChange('day', dateValue);
    setShowDayPicker(false);
  };
  
  const handleToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    // Store in YYYY-MM-DD format
    const dateValue = `${year}-${month}-${day}`;
    
    formFieldsRef.current.day = dateValue;
    handleFieldChange('day', dateValue);
    setShowDayPicker(false);
  };
  
  const handleClearDay = () => {
    formFieldsRef.current.day = '';
    handleFieldChange('day', '');
    setShowDayPicker(false);
  };
  
  const openDayPicker = () => {
    setShowDayPicker(true);
    const currentValue = formFieldsRef.current.day;
    if (currentValue) {
      // Handle both YYYY-MM-DD and mm/dd/yyyy formats
      let date;
      if (currentValue.includes('/')) {
        // mm/dd/yyyy format
        const parts = currentValue.split('/');
        if (parts.length === 3) {
          date = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
        } else {
          date = new Date(currentValue);
        }
      } else {
        // YYYY-MM-DD format
        date = new Date(currentValue);
      }
      if (!isNaN(date.getTime())) {
        setDayPickerMonth(date.getMonth() + 1);
        setDayPickerYear(date.getFullYear());
      }
    } else {
      // Initialize to current month/year if no date set
      const now = new Date();
      setDayPickerMonth(now.getMonth() + 1);
      setDayPickerYear(now.getFullYear());
    }
  };
  
  // Generate year options (current year ± 20 years)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 20; i <= currentYear + 20; i++) {
    years.push(i);
  }
  
  const calendarDays = generateCalendarDays(dayPickerMonth, dayPickerYear);
  
  // Get current selected date for highlighting
  const currentDayValue = formFieldsRef.current.day;
  let selectedDay = null;
  let selectedMonth = null;
  let selectedYear = null;
  if (currentDayValue) {
    const date = new Date(currentDayValue);
    if (!isNaN(date.getTime())) {
      selectedDay = date.getDate();
      selectedMonth = date.getMonth() + 1;
      selectedYear = date.getFullYear();
    }
  }
  
  // Handler for form field changes - update both state and ref
  const handleFieldChange = (fieldName, value) => {
    // Update ref immediately (preserves value even if re-render happens)
    formFieldsRef.current[fieldName] = value;
    // Update state for controlled components
    setFormData(prev => {
      const newData = { ...prev, [fieldName]: value };
      formDataRef.current = newData;
      return newData;
    });
  };

  // Load confirmed guardians from viewing.confirmations when in edit mode
  React.useEffect(() => {
    if (isEdit && viewing && viewing.confirmations && Array.isArray(viewing.confirmations)) {
      console.log('🔍 [PROPERTY FORM] Loading confirmed guardians from viewing.confirmations:', viewing.confirmations);
      
      // Extract guardian data from confirmations
      const confirmedGuardians = viewing.confirmations
        .filter(c => c.guardian && c.guardian.user) // Only include guardians with valid user data
        .map(c => {
          // Handle different user data structures (fullName vs first_name/last_name)
          const guardianName = c.guardian.user.fullName || 
            `${c.guardian.user.first_name || ''} ${c.guardian.user.last_name || ''}`.trim() ||
            'Unknown Guardian';
          
          // Capitalize status name for display
          const statusName = c.statusName || c.status || 'going';
          const capitalizedStatus = statusName.charAt(0).toUpperCase() + statusName.slice(1).replace('_', ' ');
          
          return {
            id: c.guardian.id,
            name: guardianName,
            email: c.guardian.user.email || '',
            status: capitalizedStatus,
            confirmationId: c.id
          };
        });
      
      console.log('✅ [PROPERTY FORM] Extracted confirmed guardians:', confirmedGuardians);
      setSelectedGuardians(confirmedGuardians);
    } else if (isEdit && viewing && !viewing.confirmations) {
      // No confirmations found
      console.log('⚠️ [PROPERTY FORM] No confirmations found in viewing data');
      setSelectedGuardians([]);
    }
  }, [isEdit, viewing]);
  
  // NO STATE - Use direct DOM manipulation only
  let propertySuggestions = [];
  let showSuggestions = false;
  let guardianSuggestions = [];
  let showGuardianSuggestions = false;

//test
  // Expose submit function for Modal
  React.useEffect(() => {
    window.propertyViewingFormSubmit = () => {
      const form = document.querySelector('.viewing-form-container');
      
      // Get current selected guardians from ref (always has latest value)
      const currentGuardians = selectedGuardiansRef.current || [];
      
      // Get property data - check refs first (most reliable), then pre-filled, then DOM
      const property_id = formFieldsRef.current.property_id || viewing.property_id || propertyInputRef.current?.closest('div')?.querySelector('[name="property_id"]')?.value || document.querySelector('[name="property_id"]')?.value || '';
      const property_title = formFieldsRef.current.property_title || propertyInputRef.current?.value || viewing.property_title || document.querySelector('[name="property_title"]')?.value || '';
      
      const data = {
        property_id: property_id,
        property_title: property_title,
        user_id: formDataRef.current.user_id || document.querySelector('[name="user_id"]')?.value || user?.id || '',
        attendees: formDataRef.current.attendees || document.querySelector('[name="attendees"]')?.value || '',
        rooms_taken: formDataRef.current.rooms_taken || document.querySelector('[name="rooms_taken"]')?.value || '',
        day: formDataRef.current.day || document.querySelector('[name="day"]')?.value || '',
        time_from: formDataRef.current.time_from || document.querySelector('[name="time_from"]')?.value || '',
        time_to: formDataRef.current.time_to || document.querySelector('[name="time_to"]')?.value || '',
        guardian_title: guardianInputRef.current?.value || formDataRef.current.guardian_title || document.querySelector('[name="guardian_title"]')?.value || '',
        guardians: currentGuardians.map(g => g.id),
        notes: formFieldsRef.current.notes || formDataRef.current.notes || document.querySelector('[name="notes"]')?.value || ''
      };
      
      console.log('🔍 [FORM] Selected guardians:', currentGuardians);
      console.log('🔍 [FORM] Guardians in data:', data.guardians);

      // Convert date format if needed
      if (data.day && data.day.includes('-')) {
        const [year, month, day] = data.day.split('-');
        data.day = `${day}/${month}/${year}`;
      }

      console.log('🔍 [FORM] Submitting:', data);
      
      // Enhanced validation with detailed logging
      console.log('🔍 [VALIDATION] Checking data:', data);
      
      // Check if this is being used for lunch event (from LunchForm)
      const isLunchSubmission = window.lunchFormSubmit && window.lunchFormSubmit === window.propertyViewingFormSubmit;
      console.log('🔍 [VALIDATION] Is lunch submission?', isLunchSubmission);
      
      // Different validation for lunch events vs property viewings
      if (!isLunchSubmission) {
        // Property viewing validation - requires property
        if (!data.property_title || !data.property_id) {
          alert('Please select a property from the suggestions');
          return false;
        }
      } else {
        // Lunch event validation - no property required
        console.log('🍽️ [VALIDATION] Lunch event - skipping property validation');
      }
      
      // Common validation for both types
      if (!data.user_id) {
        alert('User ID is missing');
        return false;
      }
      if (!data.day) {
        alert('Day is required');
        return false;
      }
      if (!data.time_from) {
        alert('Time from is required');
        return false;
      }
      
      console.log('✅ [VALIDATION] All validations passed, submitting to backend');

      onSubmit(data);
      return true;
    };
  }, [user, onSubmit, formData]); // Include formData in dependencies

  const handlePropertySearch = async (e) => {
    const value = e.target.value;
    console.log('🔍 [PROPERTY SEARCH] Typing:', value, '- NO STATE CHANGES');
    
    // Update ref immediately to preserve value
    formFieldsRef.current.property_title = value;
    
    // Clear hidden property_id field when typing
    formFieldsRef.current.property_id = '';
    const propertyIdInput = document.querySelector('[name="property_id"]');
    if (propertyIdInput) propertyIdInput.value = '';
    
    // Show/hide suggestions by directly manipulating DOM
    const suggestionsDiv = document.querySelector('.property-suggestions');
    
    if (value.length > 1) {
      try {
        const properties = await propertyViewingsService.getPropertiesForSearch(value);
        console.log('✅ Properties found:', properties);
        
        if (properties.length > 0 && suggestionsDiv) {
          suggestionsDiv.innerHTML = properties.map(property => 
            `<div class="suggestion-item" data-id="${property.id}" data-name="${property.name}" data-postcode="${property.postcode}">
              <div class="property-name">${property.name}</div>
              <div class="property-postcode">${property.postcode}</div>
            </div>`
          ).join('');
          suggestionsDiv.style.display = 'block';
        } else if (suggestionsDiv) {
          suggestionsDiv.innerHTML = '<div class="suggestions-empty">No properties found</div>';
          suggestionsDiv.style.display = 'block';
        }
      } catch (error) {
        console.error('❌ Property search error:', error);
      }
    } else {
      if (suggestionsDiv) {
        suggestionsDiv.style.display = 'none';
      }
    }
  };

  const selectProperty = (property) => {
    console.log('🔍 Selecting property:', property);
    
    // Update ref immediately to preserve value across re-renders
    const propertyValue = `${property.name} (${property.postcode})`;
    formFieldsRef.current.property_title = propertyValue;
    formFieldsRef.current.property_id = property.id;
    
    // Direct DOM manipulation to update inputs
    const propertyInput = propertyInputRef.current || document.querySelector('[name="property_title"]');
    const propertyIdInput = document.querySelector('[name="property_id"]');
    
    if (propertyInput) {
      propertyInput.value = propertyValue;
    }
    if (propertyIdInput) {
      propertyIdInput.value = property.id;
    }
    
    // Hide suggestions
    const suggestionsDiv = document.querySelector('.property-suggestions');
    if (suggestionsDiv) {
      suggestionsDiv.style.display = 'none';
    }
    console.log('✅ Property selected:', property.name, 'ID:', property.id);
  };

  // Debounce timer for guardian search
  const guardianSearchTimeoutRef = useRef(null);
  
  const handleGuardianSearch = useCallback(async (e) => {
    const value = e.target.value;
    console.log('🔍 [GUARDIAN SEARCH] Input value:', value);
    
    // Clear previous timeout
    if (guardianSearchTimeoutRef.current) {
      clearTimeout(guardianSearchTimeoutRef.current);
    }
    
    // Use ref for suggestions div, with fallback to querySelector
    let suggestionsDiv = guardianSuggestionsRef.current;
    if (!suggestionsDiv) {
      // Fallback: find the suggestions div relative to the input
      const inputContainer = e.target.closest('div[style*="position: relative"]');
      suggestionsDiv = inputContainer ? inputContainer.querySelector('.guardian-suggestions') : null;
    }
    console.log('🔍 [GUARDIAN SEARCH] Suggestions div ref:', suggestionsDiv);
    
    if (value.length >= 2) {
      // Debounce the API call to avoid too many requests
      guardianSearchTimeoutRef.current = setTimeout(async () => {
        // Re-check suggestions div in case ref wasn't set yet
        let div = guardianSuggestionsRef.current;
        if (!div) {
          const inputContainer = guardianInputRef.current?.closest('div[style*="position: relative"]');
          div = inputContainer ? inputContainer.querySelector('.guardian-suggestions') : null;
        }
        
        if (!div) {
          console.error('❌ [GUARDIAN SEARCH] Cannot find suggestions div!');
          return;
        }
        
        try {
          console.log('🔍 [GUARDIAN SEARCH] Making API call for:', value);
          const guardians = await propertyViewingsService.getGuardiansForSearch(value);
          console.log('✅ [GUARDIAN SEARCH] Guardians found:', guardians);
          console.log('✅ [GUARDIAN SEARCH] Suggestions div:', div);
          
          if (guardians && Array.isArray(guardians) && guardians.length > 0) {
            const html = guardians.map(guardian => 
              `<div class="suggestion-item" data-id="${guardian.id || ''}" data-name="${guardian.name || ''}" data-email="${guardian.email || ''}">
                <div class="guardian-name">${guardian.name || 'Unknown'}</div>
                <div class="guardian-email">${guardian.email || 'No email'}</div>
              </div>`
            ).join('');
            div.innerHTML = html;
            div.style.display = 'block';
            console.log('✅ [GUARDIAN SEARCH] Suggestions displayed, HTML length:', html.length);
          } else {
            div.innerHTML = '<div class="suggestions-empty">No guardians found</div>';
            div.style.display = 'block';
            console.log('⚠️ [GUARDIAN SEARCH] No guardians found');
          }
        } catch (error) {
          console.error('❌ [GUARDIAN SEARCH] Error:', error);
          div.innerHTML = '<div class="suggestions-empty">Error loading suggestions</div>';
          div.style.display = 'block';
        }
      }, 300); // 300ms debounce
    } else {
      const div = guardianSuggestionsRef.current || suggestionsDiv;
      if (div) {
        div.style.display = 'none';
        div.innerHTML = '';
      }
    }
  }, []);

  const selectGuardian = React.useCallback((guardian) => {
    console.log('🔍 Selecting guardian:', guardian);
    
    // Use ref to check if guardian is already added (avoids stale closure)
    const currentGuardians = selectedGuardiansRef.current || [];
    const isAlreadyAdded = currentGuardians.some(
      (g) => g.name === guardian.name && g.email === guardian.email
    );

    if (isAlreadyAdded) {
      console.log('⚠️ Guardian already added:', guardian.name);
      // Clear input but don't add duplicate
      if (guardianInputRef.current) {
        guardianInputRef.current.value = '';
      }
      if (guardianSuggestionsRef.current) {
        guardianSuggestionsRef.current.style.display = 'none';
        guardianSuggestionsRef.current.innerHTML = '';
      }
      return;
    }

    // Add guardian to the list using functional update
    // This ensures we're working with the latest state
    setSelectedGuardians((prev) => {
      const newGuardians = [
        ...prev,
        {
          id: guardian.id || Date.now(), // Use timestamp as fallback ID
          name: guardian.name,
          email: guardian.email,
        },
      ];
      // Update ref immediately for next check
      selectedGuardiansRef.current = newGuardians;
      return newGuardians;
    });

    // Clear input to allow adding more guardians
    // DON'T update formData here - it causes unnecessary re-render
    if (guardianInputRef.current) {
      guardianInputRef.current.value = '';
    }
    
    // Hide suggestions
    if (guardianSuggestionsRef.current) {
      guardianSuggestionsRef.current.style.display = 'none';
      guardianSuggestionsRef.current.innerHTML = '';
    }
    
    console.log('✅ Guardian added:', guardian.name);
  }, []); // Empty deps - use refs for all data access

  const removeGuardian = (index) => {
    setSelectedGuardians((prev) => prev.filter((_, i) => i !== index));
    console.log('✅ Guardian removed at index:', index);
  };

  const FormGroup = ({ label, children, required = false }) => (
    <div style={{display: 'table', width: '100%', marginBottom: '8px'}}>
      <label style={{display: 'table-cell', width: '150px', textAlign: 'right', paddingRight: '10px', verticalAlign: 'middle', fontSize: '12px'}}>
        {label} {required && <span style={{color: 'red'}}>*</span>}
      </label>
      <div style={{display: 'table-cell', verticalAlign: 'middle'}}>
        {children}
      </div>
    </div>
  );

  const inputStyle = {
    width: '300px',
    height: '30px',
    fontSize: '12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '6px 12px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: 'white'
  };

  return (
    <div className="viewing-form-container" style={{padding: '20px'}}>
      <FormGroup label="Property" required>
        {viewing.property_id && viewing.property_title ? (
          // Property is pre-filled (property viewings) - show as read-only
          <>
            <input name="property_id" type="hidden" defaultValue={viewing.property_id || ''} />
            <input name="property_title" type="hidden" defaultValue={viewing.property_title || ''} />
            <div style={{
              ...inputStyle,
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              padding: '6px 12px',
              display: 'inline-block',
              minHeight: '30px',
              lineHeight: '18px'
            }}>
              {viewing.property_title}
            </div>
          </>
        ) : (
          // Property is not pre-filled (user viewings) - show typeahead
          <div style={{position: 'relative'}}>
            <input
              ref={propertyInputRef}
              name="property_title"
              type="text"
              placeholder="Type property ID or name (or *)"
              style={inputStyle}
              defaultValue={formFieldsRef.current.property_title || viewing.property_title || ''}
              onChange={handlePropertySearch}
            />
            <input name="property_id" type="hidden" defaultValue={viewing.property_id || ''} />
            <div 
              className="property-suggestions"
              onClick={(e) => {
                // Find the closest suggestion-item element (handles nested divs)
                const suggestionItem = e.target.closest('.suggestion-item');
                if (suggestionItem) {
                  const id = suggestionItem.getAttribute('data-id');
                  const name = suggestionItem.getAttribute('data-name');
                  const postcode = suggestionItem.getAttribute('data-postcode');
                  selectProperty({ id, name, postcode });
                }
              }}
            ></div>
          </div>
        )}
      </FormGroup>

      {/* Postcode field - only show when property is pre-filled */}
      {viewing.property_id && viewing.postcode && (
        <FormGroup label="Postcode">
          <div style={{
            ...inputStyle,
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            padding: '6px 12px',
            display: 'inline-block',
            minHeight: '30px',
            lineHeight: '18px'
          }}>
            {viewing.postcode}
          </div>
        </FormGroup>
      )}

      <FormGroup label="User" required>
        {users && Array.isArray(users) && users.length > 0 ? (
          // Property viewings: Show user dropdown (use defaultValue + ref to prevent reset)
          <select
            name="user_id"
            required
            style={inputStyle}
            defaultValue={formFieldsRef.current.user_id}
            onChange={(e) => {
              const value = e.target.value;
              formFieldsRef.current.user_id = value; // Update ref immediately
              handleFieldChange('user_id', value);
            }}
          >
            <option value="">Please select</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.fullName || `${u.first_name || ''} ${u.last_name || ''}`.trim()}
              </option>
            ))}
          </select>
        ) : user ? (
          // User viewings: Show fixed user
          <>
            <input name="user_id" type="hidden" value={user?.id || ''} />
            <span style={{fontSize: '12px', color: '#333'}}>
              {user?.fullName || user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Michael Wilson'}
            </span>
          </>
        ) : (
          // No users available - show empty dropdown
          <select
            name="user_id"
            required
            style={inputStyle}
            value={formData.user_id}
            onChange={(e) => handleFieldChange('user_id', e.target.value)}
          >
            <option value="">Please select</option>
            <option value="" disabled>No users available</option>
          </select>
        )}
      </FormGroup>

      <FormGroup label="Confirmed">
        <input
          name="confirmed"
          type="text"
          disabled
          style={{...inputStyle, backgroundColor: '#f5f5f5'}}
          value={formData.confirmed}
        />
      </FormGroup>

      <FormGroup label="Attendees">
        <input
          name="attendees"
          type="text"
          placeholder="Enter number"
          style={inputStyle}
          defaultValue={formFieldsRef.current.attendees}
          onChange={(e) => {
            const value = e.target.value;
            formFieldsRef.current.attendees = value; // Update ref immediately
            handleFieldChange('attendees', value);
          }}
        />
      </FormGroup>

      <FormGroup label="Rooms Taken">
        <input
          name="rooms_taken"
          type="text"
          placeholder="Enter number"
          style={inputStyle}
          defaultValue={formFieldsRef.current.rooms_taken}
          onChange={(e) => {
            const value = e.target.value;
            formFieldsRef.current.rooms_taken = value; // Update ref immediately
            handleFieldChange('rooms_taken', value);
          }}
        />
      </FormGroup>

      <FormGroup label="Day" required>
        <div style={{position: 'relative', display: 'inline-block'}} ref={dayPickerRef}>
          <div style={{position: 'relative', display: 'inline-block'}}>
            <input
              name="day"
              type="text"
              placeholder="mm/dd/yyyy"
              style={inputStyle}
              value={formFieldsRef.current.day ? (() => {
                const date = new Date(formFieldsRef.current.day);
                if (!isNaN(date.getTime())) {
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const year = date.getFullYear();
                  return `${month}/${day}/${year}`;
                }
                return formFieldsRef.current.day;
              })() : ''}
              readOnly
              onClick={openDayPicker}
              onChange={() => {}} // Read-only, onChange does nothing
            />
            <span 
              className="glyphicon glyphicon-calendar" 
              onClick={openDayPicker}
              style={{ 
                position: 'absolute', 
                right: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#999',
                cursor: 'pointer',
                pointerEvents: 'auto',
                zIndex: 11,
                fontSize: '14px'
              }} 
            />
          </div>
          {showDayPicker && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 9999,
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              padding: '10px',
              marginTop: '2px',
              minWidth: '280px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px',
                paddingBottom: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePrevMonth();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '12px',
                    color: '#333',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    lineHeight: 1,
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ▲
                </button>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', flex: 1}}>
                  <select
                    value={dayPickerMonth}
                    onChange={handleMonthChange}
                    style={{
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#333',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      outline: 'none',
                      minWidth: '100px'
                    }}
                  >
                    {monthNames.map((month, index) => (
                      <option key={index} value={index + 1}>{month}</option>
                    ))}
                  </select>
                  <select
                    value={dayPickerYear}
                    onChange={handleYearChange}
                    style={{
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#333',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      outline: 'none',
                      minWidth: '70px'
                    }}
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNextMonth();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '12px',
                    color: '#333',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    lineHeight: 1,
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ▼
                </button>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '2px',
                marginBottom: '5px'
              }}>
                {dayNames.map(day => (
                  <div key={day} style={{
                    textAlign: 'center',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#666',
                    padding: '4px 0'
                  }}>{day}</div>
                ))}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '2px',
                marginBottom: '10px'
              }}>
                {calendarDays.map((day, index) => (
                  <div
                    key={index}
                    onClick={() => day !== null && handleDaySelect(day)}
                    style={{
                      textAlign: 'center',
                      fontSize: '13px',
                      padding: '8px 4px',
                      cursor: day !== null ? 'pointer' : 'default',
                      borderRadius: '4px',
                      color: day === null ? 'transparent' : 
                             (selectedDay === day && selectedMonth === dayPickerMonth && selectedYear === dayPickerYear) ? 'white' : '#333',
                      backgroundColor: (selectedDay === day && selectedMonth === dayPickerMonth && selectedYear === dayPickerYear) ? '#337ab7' : 'transparent',
                      transition: 'background-color 0.2s, color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (day !== null && !(selectedDay === day && selectedMonth === dayPickerMonth && selectedYear === dayPickerYear)) {
                        e.target.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (day !== null && !(selectedDay === day && selectedMonth === dayPickerMonth && selectedYear === dayPickerYear)) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {day}
                  </div>
                ))}
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '10px',
                borderTop: '1px solid #e9ecef'
              }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleToday();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#337ab7',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '4px 8px'
                  }}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearDay();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#d9534f',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '4px 8px'
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
      </FormGroup>

      <FormGroup label="Time From" required>
        <input
          name="time_from"
          type="time"
          min="09:00"
          style={inputStyle}
          defaultValue={formFieldsRef.current.time_from}
          onChange={(e) => {
            const value = e.target.value;
            formFieldsRef.current.time_from = value; // Update ref immediately
            handleFieldChange('time_from', value);
          }}
        />
      </FormGroup>

      <FormGroup label="Time To" required>
        <input
          name="time_to"
          type="time"
          style={inputStyle}
          defaultValue={formFieldsRef.current.time_to}
          onChange={(e) => {
            const value = e.target.value;
            formFieldsRef.current.time_to = value; // Update ref immediately
            handleFieldChange('time_to', value);
          }}
        />
      </FormGroup>

      <FormGroup label="Type guardian's email or name to add">
        <div style={{position: 'relative', zIndex: 1}}>
          <input
            ref={guardianInputRef}
            name="guardian_title"
            type="text"
            placeholder="Type guardian's name or email"
            style={inputStyle}
            defaultValue={viewing.guardian_title || ''}
            onChange={handleGuardianSearch}
          />
          <div
            ref={guardianSuggestionsRef}
            className="guardian-suggestions"
            style={{display: 'none', position: 'absolute', zIndex: 9998}}
            onClick={(e) => {
              // Find the closest suggestion-item element (handles nested divs)
              const suggestionItem = e.target.closest('.suggestion-item');
              if (suggestionItem) {
                const id = suggestionItem.getAttribute('data-id');
                const name = suggestionItem.getAttribute('data-name');
                const email = suggestionItem.getAttribute('data-email');
                selectGuardian({ id, name, email });
              }
            }}
          ></div>
        </div>
      </FormGroup>

      {/* Selected Guardians List - Laravel Style */}
      {selectedGuardians.length > 0 && (
        <FormGroup label={isEdit ? "Guardians confirmed" : ""}>
          <div style={{
            marginTop: '10px',
            padding: '10px',
            backgroundColor: '#f9f9f9',
            borderRadius: '4px',
            border: '1px solid #ddd'
          }}>
            {isEdit ? (
              // Edit mode: Show confirmed guardians in table format (Laravel style)
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginTop: '10px'
              }}>
                <thead>
                  <tr style={{
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #ddd'
                  }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px' }}>Guardian</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedGuardians.map((guardian, index) => (
                    <tr 
                      key={guardian.id || index}
                      style={{
                        borderBottom: '1px solid #ddd'
                      }}
                    >
                      <td style={{ padding: '8px', fontSize: '12px' }}>
                        {guardian.name}
                      </td>
                      <td style={{ padding: '8px', fontSize: '12px' }}>
                        {guardian.status || 'Confirmed'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // Add mode: Show selected guardians with delete buttons
              selectedGuardians.map((guardian, index) => (
                <div 
                  key={guardian.id || index} 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px',
                    marginBottom: '5px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                >
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    {guardian.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeGuardian(index)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#dc3545',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      fontSize: '14px',
                      marginLeft: '10px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.textDecoration = 'underline';
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.borderRadius = '3px';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.textDecoration = 'none';
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </FormGroup>
      )}
      
      {/* Show message when no confirmed guardians in edit mode */}
      {isEdit && (!viewing.confirmations || viewing.confirmations.length === 0) && (
        <FormGroup label="Guardians confirmed">
          <p style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', marginTop: '10px' }}>
            There is no confirmed guardian!
          </p>
        </FormGroup>
      )}

      <FormGroup label="Notes">
        <textarea
          name="notes"
          rows="4"
          style={{
            ...inputStyle,
            height: '80px',
            resize: 'none'
          }}
          value={formData.notes}
          onChange={(e) => handleFieldChange('notes', e.target.value)}
        />
      </FormGroup>

      <FormGroup label="Updated At">
        <input
          name="updated_at"
          type="text"
          disabled
          style={{...inputStyle, backgroundColor: '#f5f5f5'}}
          defaultValue={viewing.updated_at || ''}
        />
      </FormGroup>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  // Only re-render if critical props actually changed
  return (
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.isEdit === nextProps.isEdit &&
    prevProps.viewing?.id === nextProps.viewing?.id &&
    prevProps.viewing?.user_id === nextProps.viewing?.user_id &&
    JSON.stringify(prevProps.users) === JSON.stringify(nextProps.users)
  );
});

export default PropertyViewingForm;
